import type { Company } from "@domain/CRM/company";
import type { Grower } from "@domain/CRM/grower";
import type { Job } from "@domain/CRM/job";
import type { User } from "@domain/Auth";
import { applyCollectBoundary } from "@domain/CRM/job";
import type { LogData } from "@rogoag/shared-ts/dist/logger";
import type { ILogger } from "@ports/logger/ILogger";

interface MarkJobsReadyUseCasesDeps {
	updateJobs(jobs: Job[], compressed?: boolean): Promise<void>;
	/** Optional read ports — only the page wires them; the popover doesn't need them. The
	 *  optional `fresh` flag on `getGrowersWithUnreadyJobs` bypasses the server-side read
	 *  cache while still repopulating it — see `useFreshOnEntry`. `getJobsForGrowers` reads
	 *  the uncached Jobs table and has no `fresh` param to match. */
	getGrowersWithUnreadyJobs?(company: Company, fresh?: boolean): Promise<Grower[]>;
	getJobsForGrowers?(company: Company, growers: Grower[]): Promise<Job[]>;
	logger: ILogger;
}

/**
 * Mark a batch of jobs as ready: stamps each job's `dateMarkedReady`, applies
 * the `collectBoundary` domain transformation, and bulk-updates them in a
 * single write.
 *
 * Readiness now lives directly on the Job (the `Marked Ready` Airtable date)
 * rather than in a separate Job Ready record — see the Job Ready table
 * retirement. Callers see a single thrown error on failure; recovery is the
 * UI's responsibility.
 */
export interface MarkJobsReadyUseCases {
	/** Throws if `selectedJobs` is empty — the caller's UI guards that, but
	 *  we preserve the existing contract. */
	submitForm(selectedJobs: Job[], user?: User): Promise<void>;
	loadGrowersWithUnreadyJobs(company: Company, fresh?: boolean): Promise<Grower[]>;
	/** `company`'s unready jobs for `growers` — never another company's, even for a grower
	 *  that both companies' jobs link. */
	loadJobsForGrowers(company: Company, growers: Grower[]): Promise<Job[]>;
}

async function submitForm(deps: MarkJobsReadyUseCasesDeps, selectedJobs: Job[], user?: User): Promise<void> {
	if (!selectedJobs || selectedJobs.length === 0) {
		throw new Error("selectedJobs.length == 0");
	}
	const jobIds = selectedJobs.filter((j) => !!j).map((j) => j.id);
	const logData: LogData = {
		function: "markJobsReady.submitForm",
		airtable_record_id: jobIds.join(","),
		airtable_table: "Jobs",
	};
	deps.logger.log(`Marking ${jobIds.length} jobs ready${user?.name ? ` (by ${user.name})` : ""}`, logData);

	// Audit-warn if any selected job is already marked — preserves the
	// pre-existing diagnostic surface.
	for (const job of selectedJobs) {
		if (job?.isMarkedReady) {
			deps.logger.warning(`Job ${job.id} is already marked as ready, but is being marked again`, {
				...logData,
				airtable_record_id: job.id,
			});
		}
	}

	// Readiness is stamped directly on the Job. One timestamp for the whole
	// batch so a single submission shares a marking time.
	const markedAt = new Date();
	for (const job of selectedJobs) {
		job.dateMarkedReady = markedAt;
		job.isMarkedReady = true;
		applyCollectBoundary(job);
	}

	await deps.updateJobs(selectedJobs, true);
	deps.logger.log("Jobs updated successfully after marking as ready", logData);
}

function loadGrowersWithUnreadyJobs(
	deps: MarkJobsReadyUseCasesDeps,
	company: Company,
	fresh?: boolean,
): Promise<Grower[]> {
	if (!deps.getGrowersWithUnreadyJobs) {
		throw new Error("markJobsReady: getGrowersWithUnreadyJobs dep not wired.");
	}
	return deps.getGrowersWithUnreadyJobs(company, fresh);
}

function loadJobsForGrowers(deps: MarkJobsReadyUseCasesDeps, company: Company, growers: Grower[]): Promise<Job[]> {
	if (!deps.getJobsForGrowers) {
		throw new Error("markJobsReady: getJobsForGrowers dep not wired.");
	}
	return deps.getJobsForGrowers(company, growers);
}

export function makeMarkJobsReadyUseCases(deps: MarkJobsReadyUseCasesDeps): MarkJobsReadyUseCases {
	return {
		submitForm: (selectedJobs, user) => submitForm(deps, selectedJobs, user),
		loadGrowersWithUnreadyJobs: (company, fresh) => loadGrowersWithUnreadyJobs(deps, company, fresh),
		loadJobsForGrowers: (company, growers) => loadJobsForGrowers(deps, company, growers),
	};
}
